import { requestMagicLink } from "@/api/services/authentication"
import { loginSchema } from "@/schemas/onboarding/loginSchema"
import { handleMutationError, scrollToFirstFormError } from "toptaal-shared"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { Button, Input, LoginWrapper, useAppForm } from "toptaal-shared"
import { useAuthStore } from "@/stores/useAuthStore"
import { useLoginMethodStore } from "@/stores/useLoginMethodStore"

export const Route = createFileRoute("/login/")({
  component: RouteComponent,
  beforeLoad: async () => {
    const { getToken, getRefreshToken } = useAuthStore.getState()
    if (getToken() || getRefreshToken()) {
      throw redirect({ to: "/" })
    }

    const { method } = useLoginMethodStore.getState()
    if (method === "google_sso") {
      throw redirect({ to: "/login/google_sso" })
    }
    if (method === "microsoft_sso") {
      throw redirect({ to: "/login/microsoft_sso" })
    }
  },
})

function RouteComponent() {
  const router = useRouter()
  const [error, setError] = useState<string | null>(null)
  const [showMagicLinkSent, setShowMagicLinkSent] = useState(false)
  const { setMethod } = useLoginMethodStore()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (email: string) => {
      return await requestMagicLink(email)
    },
  })

  const form = useAppForm({
    onSubmitInvalid: scrollToFirstFormError,
    validators: {
      onSubmit: loginSchema,
    },
    onSubmit: async ({ value }) => {
      mutateAsync(value.email)
        .then(({ method }) => {
          setMethod(method)

          if (method === "magic_link") {
            setShowMagicLinkSent(true)

            return
          }

          router.navigate({
            to:
              method === "google_sso"
                ? "/login/google_sso"
                : "/login/microsoft_sso",
          })
        })
        .catch(handleMutationError(setError))
    },
    defaultValues: {
      email: "",
    },
  })

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      form.handleSubmit()
    },
    [form],
  )

  return (
    <LoginWrapper title="Inloggen">
      {showMagicLinkSent ? (
        <p className="text-center">
          We hebben een link naar je e-mailadres gestuurd waarmee je kunt
          inloggen.
        </p>
      ) : (
        <>
          <form.AppForm>
            <form
              onSubmit={handleSubmit}
              onChange={() => {
                if (error) {
                  setError(null)
                }
              }}
            >
              <form.AppField
                name="email"
                children={(field) => (
                  <field.FormItem>
                    <field.FormControl>
                      <Input
                        label="E-mailadres"
                        type="text"
                        variant={"grey"}
                        className="w-full"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="email"
                      />
                    </field.FormControl>
                    {error && <p className="text-red text-sm mt-2">{error}</p>}

                    <field.FormMessage />
                  </field.FormItem>
                )}
              />

              <form.Subscribe>
                {({ isDirty, isValid }) => (
                  <Button
                    type="submit"
                    disabled={!isDirty || !isValid}
                    variant="ghost"
                    isLoading={isPending}
                    className="flex mx-auto mt-10"
                  >
                    Inloggen
                  </Button>
                )}
              </form.Subscribe>
            </form>
          </form.AppForm>
        </>
      )}
    </LoginWrapper>
  )
}
